import { createDecorator } from "../../instantiation/common/registry.js";
import { SpaceMouseDeviceState } from "../../hid/common/spaceMouse.js";
import type { IKeyboardService, KeyCode } from "../../keyboard/common/keyboardService.js";

export interface NavigationVector3 {
    x: number;
    y: number;
    z: number;
}

export interface NavigationDeviceIntent {
    move: NavigationVector3;
    rotate: NavigationVector3;
    boost: boolean;
    slow: boolean;
    reset: boolean;
    select: boolean;
    focus: boolean;
}

export const emptyNavigationDeviceIntent = (): NavigationDeviceIntent => ({
    move: { x: 0, y: 0, z: 0 },
    rotate: { x: 0, y: 0, z: 0 },
    boost: false,
    slow: false,
    reset: false,
    select: false,
    focus: false,
});

export const INavigationDeviceService = createDecorator<INavigationDeviceService>("navigationDeviceService");

export interface NavigationIntentOptions {
    /**
     * Accept KEYBOARD navigation only while this window has focus. Pressed-key
     * state is global on purpose (`isKeyPressed` tracks every window), so a
     * consumer whose viewport lives in ONE window would otherwise be driven by
     * keys pressed in another — e.g. WASD typed into a popout ("view in new
     * window") flew the main viewport's camera. Omit to accept keyboard input
     * regardless of which window has focus. Gamepad / SpaceMouse are not gated:
     * they aren't bound to a window at all.
     */
    window?: Window | null;
    /**
     * Whether WASD / Q / E may drive keyboard movement. The editor camera passes
     * `false` while no camera-look mouse button is held: those letters double as
     * TOOL shortcuts (gizmo W/E, later R scale), so — Unity/Unreal-style — they
     * fly only while the right mouse button is down. ARROW KEYS always fly
     * regardless. Omit for full keyboard movement.
     */
    wasdQE?: boolean;
}

export interface INavigationDeviceService {
    getIntent(options?: NavigationIntentOptions): NavigationDeviceIntent;
    requestSpaceMouseDevice(): Promise<boolean>;
}

type KeyboardNavigationSource = Pick<IKeyboardService, "isKeyPressed">;

export function combineNavigationDeviceIntent(...intents: readonly NavigationDeviceIntent[]): NavigationDeviceIntent {
    const combined = emptyNavigationDeviceIntent();
    for (const intent of intents) {
        combined.move.x += intent.move.x;
        combined.move.y += intent.move.y;
        combined.move.z += intent.move.z;
        combined.rotate.x += intent.rotate.x;
        combined.rotate.y += intent.rotate.y;
        combined.rotate.z += intent.rotate.z;
        combined.boost = combined.boost || intent.boost;
        combined.slow = combined.slow || intent.slow;
        combined.reset = combined.reset || intent.reset;
        combined.select = combined.select || intent.select;
        combined.focus = combined.focus || intent.focus;
    }
    return combined;
}

export function clampNavigationAxis(value: number): number {
    if (!Number.isFinite(value)) return 0;
    return Math.max(-1, Math.min(1, value));
}

export function applyAxisDeadzone(value: number, deadzone: number): number {
    const clampedValue = clampNavigationAxis(value);
    const normalizedDeadzone = Math.max(0, Math.min(0.95, deadzone));
    const abs = Math.abs(clampedValue);
    if (abs <= normalizedDeadzone) return 0;
    const scaled = (abs - normalizedDeadzone) / (1 - normalizedDeadzone);
    return Math.sign(clampedValue) * scaled;
}

export function parseGamepadIntent(gamepad: Pick<Gamepad, "axes" | "buttons" | "mapping" | "connected">, deadzone: number = .18): NavigationDeviceIntent {
    if (!gamepad.connected) return emptyNavigationDeviceIntent();

    const leftX = applyAxisDeadzone(gamepad.axes[0] ?? 0, deadzone);
    const leftY = applyAxisDeadzone(gamepad.axes[1] ?? 0, deadzone);
    const rightX = applyAxisDeadzone(gamepad.axes[2] ?? 0, deadzone);
    const rightY = applyAxisDeadzone(gamepad.axes[3] ?? 0, deadzone);

    const leftTrigger = normalizeGamepadButtonValue(gamepad.buttons[6]);
    const rightTrigger = normalizeGamepadButtonValue(gamepad.buttons[7]);
    const move = clampNavigationVectorLength({
        x: leftX,
        y: rightTrigger - leftTrigger,
        z: -leftY,
    });

    return {
        move,
        rotate: {
            x: clampNavigationAxis(rightY * 2),
            y: clampNavigationAxis(-rightX * 2),
            z: 0,
        },
        boost: isGamepadButtonPressed(gamepad.buttons[4]),
        slow: isGamepadButtonPressed(gamepad.buttons[5]),
        reset: isGamepadButtonPressed(gamepad.buttons[1]),
        select: isGamepadButtonPressed(gamepad.buttons[0]),
        focus: isGamepadButtonPressed(gamepad.buttons[2]),
    };
}

export function parseSpaceMouseIntent(state: SpaceMouseDeviceState, translationScale: number = 35, rotationScale: number = 3500): NavigationDeviceIntent {
    const normalizeTranslation = (value: number) => clampNavigationAxis(value / translationScale);
    const normalizeRotation = (value: number) => clampNavigationAxis(value / rotationScale);
    return {
        move: {
            x: normalizeTranslation(state.translation.x),
            y: -normalizeTranslation(state.translation.z),
            z: normalizeTranslation(state.translation.y),
        },
        rotate: {
            x: normalizeRotation(state.rotation.x),
            y: -normalizeRotation(state.rotation.z),
            z: 0,
        },
        boost: (state.buttons & 0b10) !== 0,
        slow: (state.buttons & 0b01) !== 0,
        reset: false,
        select: false,
        focus: false,
    };
}

export function parseKeyboardIntent(keyboard: KeyboardNavigationSource, options?: { wasdQE?: boolean }): NavigationDeviceIntent {
    // WASD/QE are tool keys unless a camera-look button is held (see
    // NavigationIntentOptions.wasdQE) — arrow keys always move
    const letters = options?.wasdQE !== false;
    return {
        move: clampNavigationVectorLength({
            x: getKeyboardAxis(keyboard, letters ? ["KeyA", "ArrowLeft"] : ["ArrowLeft"], letters ? ["KeyD", "ArrowRight"] : ["ArrowRight"]),
            y: letters ? getKeyboardAxis(keyboard, ["KeyQ"], ["KeyE"]) : 0,
            z: getKeyboardAxis(keyboard, letters ? ["KeyW", "ArrowUp"] : ["ArrowUp"], letters ? ["KeyS", "ArrowDown"] : ["ArrowDown"]),
        }),
        rotate: {
            x: 0,
            y: 0,
            z: 0,
        },
        boost: keyboard.isKeyPressed("Shift"),
        slow: keyboard.isKeyPressed("Alt"),
        reset: false,
        select: false,
        focus: false,
    };
}

function normalizeGamepadButtonValue(button: GamepadButton | null | undefined): number {
    if (!button) return 0;
    return clampNavigationAxis(button.value);
}

function isGamepadButtonPressed(button: GamepadButton | null | undefined): boolean {
    if (!button) return false;
    return button.pressed || button.value > .5;
}

function getKeyboardAxis(keyboard: KeyboardNavigationSource, negativeKeys: readonly KeyCode[], positiveKeys: readonly KeyCode[]): number {
    const negative = negativeKeys.some(key => keyboard.isKeyPressed(key));
    const positive = positiveKeys.some(key => keyboard.isKeyPressed(key));
    if (negative === positive) return 0;
    return negative ? -1 : 1;
}

function clampNavigationVectorLength(vector: NavigationVector3): NavigationVector3 {
    const lengthSq = vector.x * vector.x + vector.y * vector.y + vector.z * vector.z;
    if (lengthSq <= 1 || lengthSq === 0) {
        return vector;
    }

    const inverseLength = 1 / Math.sqrt(lengthSq);
    return {
        x: vector.x * inverseLength,
        y: vector.y * inverseLength,
        z: vector.z * inverseLength,
    };
}
